import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Chip,
  IconButton,
  Link,
  Divider,
  LinearProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  Article as ArticleIcon,
  Image as ImageIcon,
  School as SchoolIcon,
  OpenInNew as OpenInNewIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import { Source } from '../types';

interface SourcePanelProps {
  open: boolean;
  onClose: () => void;
  sources: Source[];
  width: number;
}

const SourcePanel: React.FC<SourcePanelProps> = ({
  open,
  onClose,
  sources,
  width,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const getSourceIcon = (sourceType: string) => {
    switch (sourceType) {
      case 'franconnect':
        return <ArticleIcon />;
      case 'widen':
        return <ImageIcon />;
      case 'litmos':
        return <SchoolIcon />;
      default:
        return <ArticleIcon />;
    }
  };

  const getSourceColor = (sourceType: string) => {
    switch (sourceType) {
      case 'franconnect':
        return '#00ACDD';
      case 'widen':
        return '#FF6B35';
      case 'litmos':
        return '#10B981';
      default:
        return '#64748B';
    }
  };

  const getSourceLabel = (sourceType: string) => {
    switch (sourceType) {
      case 'franconnect':
        return 'FranConnect';
      case 'widen':
        return 'Widen DAM';
      case 'litmos':
        return 'Litmos LMS';
      default:
        return sourceType;
    }
  };

  const getConfidenceColor = (score: number) => {
    if (score >= 0.8) return 'success';
    if (score >= 0.6) return 'warning';
    return 'error';
  };

  const drawerContent = (
    <Box sx={{ width: width, height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid #E0E0E0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VerifiedIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Sources
          </Typography>
        </Box>
        {isMobile && (
          <IconButton 
            onClick={onClose} 
            size="small"
            sx={{ 
              color: 'text.secondary',
              '&:hover': { backgroundColor: 'grey.100' },
              zIndex: 1000,
              p: 1.5,
            }}
            aria-label="Close sources panel"
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {sources.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              textAlign: 'center',
              color: 'text.secondary',
            }}
          >
            <VerifiedIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
            <Typography variant="body2">
              No sources available yet. Start a conversation to see relevant documents and resources.
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {sources.map((source, index) => (
              <ListItem key={`${source.source_id}-${index}`} sx={{ p: 0, mb: 2 }}>
                <Paper
                  elevation={1}
                  sx={{
                    width: '100%',
                    p: 2,
                    borderRadius: 2,
                    border: `2px solid ${getSourceColor(source.source_type)}20`,
                  }}
                >
                  {/* Source Header */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
                    <Box
                      sx={{
                        color: getSourceColor(source.source_type),
                        mt: 0.5,
                      }}
                    >
                      {getSourceIcon(source.source_type)}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {source.title}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Chip
                          label={getSourceLabel(source.source_type)}
                          size="small"
                          sx={{
                            bgcolor: `${getSourceColor(source.source_type)}20`,
                            color: getSourceColor(source.source_type),
                            fontWeight: 500,
                            fontSize: '0.7rem',
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>

                  {/* Content Snippet */}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {source.content_snippet}
                  </Typography>

                  {/* Confidence Score */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Relevance Score
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {Math.round(source.confidence_score * 100)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={source.confidence_score * 100}
                      color={getConfidenceColor(source.confidence_score)}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: '#E0E0E0',
                      }}
                    />
                  </Box>

                  {/* Document Link */}
                  {source.document_url && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Link
                        href={source.document_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          textDecoration: 'none',
                          color: 'primary.main',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        View Document
                        <OpenInNewIcon sx={{ fontSize: 16 }} />
                      </Link>
                    </Box>
                  )}
                </Paper>
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid #E0E0E0',
          backgroundColor: 'background.paper',
        }}
      >
        <Typography variant="caption" color="text.secondary" align="center" display="block">
          Sources are ranked by relevance to your query
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      {isMobile ? (
        <Drawer
          anchor="right"
          variant="temporary"
          open={open}
          onClose={onClose}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            zIndex: (theme) => theme.zIndex.appBar - 1,
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: width,
              pt: 8, // Padding top for header, not margin
              height: '100vh',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <Drawer
          anchor="right"
          variant="persistent"
          open={open}
          sx={{
            width: open ? width : 0,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: width,
              boxSizing: 'border-box',
              mt: 8, // Account for header height
              height: `calc(100vh - 64px)`,
              borderLeft: '1px solid #E0E0E0',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
    </>
  );
};

export default SourcePanel; 